import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Sidebar({ isMobileOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, logout } = useAuth();
  
  const fullName = userInfo?.fullName || 'Korisnik';

  // ESC tipka zatvara mobilni meni
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (isMobileOpen && onClose) {
          onClose();
        }
      }
    };

    if (isMobileOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileOpen, onClose]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const dijelovi = name.trim().split(/\s+/);
    if (dijelovi.length === 1) return dijelovi[0].charAt(0).toUpperCase();
    return (dijelovi[0].charAt(0) + dijelovi[dijelovi.length - 1].charAt(0)).toUpperCase();
  };

  const handleProfileClick = () => {
    if (onClose) onClose();
    navigate('/');
  };

  const handleLogoutClick = (e) => {
    e.stopPropagation();
    if (onClose) onClose();
    logout();
    window.location.href = '/login';
  };

  // Popis stavki izbornika: Troškovi i Troškovnik kao odvojene stavke
  const menuItems = [
    { path: '/', label: 'Nadzorna ploča', icon: 'dashboard' },
    { path: '/projekti', label: 'Stanovi', icon: 'apartment' },
    { path: '/crm', label: 'Kupci & Leadovi', icon: 'group' },
    { path: '/kalendar', label: 'Kalendar sastanaka', icon: 'calendar_month' },
    { path: '/nalozi', label: 'Radni nalozi', icon: 'assignment' },
    { path: '/aktivnosti', label: 'Aktivnosti', icon: 'history' },
    { path: '/troskovi', label: 'Troškovi', icon: 'payments' }, 
    { path: '/costs', label: 'Troškovnik', icon: 'receipt_long' }, 
    { path: '/dnevnik', label: 'Građevinski Dnevnik', icon: 'edit_note' },
  ];

  // Sadržaj izbornika (zajednički za desktop i mobile)
  const sidebarContent = (
    <div className="flex flex-col h-full justify-between">
      <div>
        {/* LOGOTIP I GUMB ZA ZATVARANJE NA MOBILNOM */}
        <div className="flex items-center justify-between mb-8 pl-2">
          <div className="flex items-center gap-3">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 100 100" 
              className="w-9 h-9 text-slate-800 flex-shrink-0"
            >
              <path d="M 10 90 L 90 90" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M 65 90 L 65 44 L 75 32 L 85 44 L 85 90" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M 55 90 L 55 64 L 65 54" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M 85 54 L 95 64 L 95 90" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <line x1="70" y1="52" x2="70" y2="56" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="70" y1="62" x2="70" y2="66" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="70" y1="72" x2="70" y2="76" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="70" y1="82" x2="70" y2="86" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="80" y1="52" x2="80" y2="56" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="80" y1="62" x2="80" y2="66" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="80" y1="72" x2="80" y2="76" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="80" y1="82" x2="80" y2="86" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="90" y1="70" x2="90" y2="74" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <line x1="90" y1="80" x2="90" y2="84" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <path d="M 10 68 L 28 50 L 38 64 L 55 48" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M 46 48 L 55 48 L 55 57" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M 18 90 L 18 78 L 24 78 L 24 90" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M 30 90 L 30 74 L 36 74 L 36 90" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            <div>
              <p className="font-extrabold text-slate-800 text-base leading-tight uppercase">DEVELOPER</p>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">ERP</p>
            </div>
          </div>

          {/* Gumb za zatvaranje na mobitelu */}
          {onClose && (
            <button 
              type="button" 
              onClick={onClose}
              className="md:hidden text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition cursor-pointer"
            >
              ✕
            </button>
          )}
        </div>

        {/* POPIS SEKCIJA */}
        <nav className="space-y-1">
          {menuItems.map(item => {
            const isActive = item.path === '/' 
              ? location.pathname === '/' 
              : location.pathname.startsWith(item.path);

            return (
              <Link 
                key={item.path} 
                to={item.path} 
                onClick={() => { if (onClose) onClose(); }}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl font-semibold text-xs transition-all duration-150 ${
                  isActive 
                    ? 'bg-slate-900 text-white font-bold shadow-sm' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <span className="material-symbols-outlined text-lg">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* PROFIL I ODJAVA */}
      <div className="pt-4 border-t border-slate-100">
        <div 
          onClick={handleProfileClick} 
          className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 transition-all cursor-pointer group"
        >
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-slate-700 to-slate-900 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                {getInitials(fullName)}
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white"></span>
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-800 leading-tight">{fullName}</p>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Pristup računu</p>
            </div>
          </div>
          <button 
            type="button"
            onClick={handleLogoutClick}
            className="text-slate-400 hover:text-red-500 transition-all p-1.5 rounded-lg hover:bg-red-50 shrink-0"
            title="Odjava"
          >
            <span className="material-symbols-outlined text-base">logout</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* DESKTOP SIDEBAR */}
      <aside className="hidden md:flex flex-col h-screen w-[260px] fixed left-0 top-0 bg-white border-r border-slate-200 z-50 p-6 justify-between">
        {sidebarContent}
      </aside>

      {/* MOBILNI SIDEBAR */}
      {isMobileOpen && (
        <div 
          onClick={onClose}
          className="md:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[999] transition-opacity"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="w-[280px] h-full bg-white p-6 shadow-2xl flex flex-col animate-in slide-in-from-left duration-200"
          >
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}